/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { CustomerInteractionSettingResponse } from '../models/customer-interaction-setting-response';
import { getSetting } from '../fn/tenant-settings/get-setting';
import { GetSetting$Params } from '../fn/tenant-settings/get-setting';
import { GroupOrderingSettingsResponse } from '../models/group-ordering-settings-response';
import { OrderProcessingStatusSettingResponse } from '../models/order-processing-status-setting-response';
import { SettingResponse } from '../models/setting-response';
import { TenantNutritionalDefaultsSettingResponse } from '../models/tenant-nutritional-defaults-setting-response';
import { upsertSetting } from '../fn/tenant-settings/upsert-setting';
import { UpsertSetting$Params } from '../fn/tenant-settings/upsert-setting';

@Injectable({ providedIn: 'root' })
export class TenantSettingsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getSetting()` */
  static readonly GetSettingPath = '/tenants/{tenantId}/settings/{id}';

  /**
   * Get Tenant Setting.
   *
   * Endpoint to fetch the value of a tenant setting
   * - **CHECKIN_URL**: Base url to be used to get checkin status
   * - **ORDER_STATUS_TRACKING_URL**: Base url to be used to get order tracking
   * - **ORDER_PROCESSING_STATUS**: Order processing status settings for Order Dashboard tracking
   * - **CUSTOMER_INTERACTION**: Customer interaction settings
   * - **GROUP_ORDERING**: Group ordering settings
   * - **BRAND_NUTRITIONAL_DEFAULTS**: Brand nutritional defaults settings
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSetting()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSetting$Response(params: GetSetting$Params, context?: HttpContext): Observable<StrictHttpResponse<(SettingResponse | OrderProcessingStatusSettingResponse | CustomerInteractionSettingResponse | GroupOrderingSettingsResponse | TenantNutritionalDefaultsSettingResponse)>> {
    return getSetting(this.http, this.rootUrl, params, context);
  }

  /**
   * Get Tenant Setting.
   *
   * Endpoint to fetch the value of a tenant setting
   * - **CHECKIN_URL**: Base url to be used to get checkin status
   * - **ORDER_STATUS_TRACKING_URL**: Base url to be used to get order tracking
   * - **ORDER_PROCESSING_STATUS**: Order processing status settings for Order Dashboard tracking
   * - **CUSTOMER_INTERACTION**: Customer interaction settings
   * - **GROUP_ORDERING**: Group ordering settings
   * - **BRAND_NUTRITIONAL_DEFAULTS**: Brand nutritional defaults settings
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getSetting$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSetting(params: GetSetting$Params, context?: HttpContext): Observable<(SettingResponse | OrderProcessingStatusSettingResponse | CustomerInteractionSettingResponse | GroupOrderingSettingsResponse | TenantNutritionalDefaultsSettingResponse)> {
    return this.getSetting$Response(params, context).pipe(
      map((r: StrictHttpResponse<(SettingResponse | OrderProcessingStatusSettingResponse | CustomerInteractionSettingResponse | GroupOrderingSettingsResponse | TenantNutritionalDefaultsSettingResponse)>): (SettingResponse | OrderProcessingStatusSettingResponse | CustomerInteractionSettingResponse | GroupOrderingSettingsResponse | TenantNutritionalDefaultsSettingResponse) => r.body)
    );
  }

  /** Path part for operation `upsertSetting()` */
  static readonly UpsertSettingPath = '/tenants/{tenantId}/settings/{id}';

  /**
   * Upsert Tenant Setting.
   *
   * Endpoint to create or update the value of a tenant setting
   * - **CHECKIN_URL**: Base url to be used to get checkin status
   * - **ORDER_STATUS_TRACKING_URL**: Base url to be used to get order tracking
   * - **ORDER_PROCESSING_STATUS**: Order processing status settings for Order Dashboard tracking
   * - **CUSTOMER_INTERACTION**: Customer interaction settings
   * - **GROUP_ORDERING**: Group ordering settings
   * - **BRAND_NUTRITIONAL_DEFAULTS**: Brand nutritional defaults settings
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `upsertSetting()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  upsertSetting$Response(params: UpsertSetting$Params, context?: HttpContext): Observable<StrictHttpResponse<(SettingResponse | OrderProcessingStatusSettingResponse | CustomerInteractionSettingResponse | GroupOrderingSettingsResponse | TenantNutritionalDefaultsSettingResponse)>> {
    return upsertSetting(this.http, this.rootUrl, params, context);
  }

  /**
   * Upsert Tenant Setting.
   *
   * Endpoint to create or update the value of a tenant setting
   * - **CHECKIN_URL**: Base url to be used to get checkin status
   * - **ORDER_STATUS_TRACKING_URL**: Base url to be used to get order tracking
   * - **ORDER_PROCESSING_STATUS**: Order processing status settings for Order Dashboard tracking
   * - **CUSTOMER_INTERACTION**: Customer interaction settings
   * - **GROUP_ORDERING**: Group ordering settings
   * - **BRAND_NUTRITIONAL_DEFAULTS**: Brand nutritional defaults settings
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `upsertSetting$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  upsertSetting(params: UpsertSetting$Params, context?: HttpContext): Observable<(SettingResponse | OrderProcessingStatusSettingResponse | CustomerInteractionSettingResponse | GroupOrderingSettingsResponse | TenantNutritionalDefaultsSettingResponse)> {
    return this.upsertSetting$Response(params, context).pipe(
      map((r: StrictHttpResponse<(SettingResponse | OrderProcessingStatusSettingResponse | CustomerInteractionSettingResponse | GroupOrderingSettingsResponse | TenantNutritionalDefaultsSettingResponse)>): (SettingResponse | OrderProcessingStatusSettingResponse | CustomerInteractionSettingResponse | GroupOrderingSettingsResponse | TenantNutritionalDefaultsSettingResponse) => r.body)
    );
  }

}
